import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { ConfirmEventTicketOrderRequest } from '@models/events/dto/confirm-event-ticket-order.request';
import {
  CreateEventTicketOrderSessionRequest,
  EventTicketOrderAdditionalDataRequest,
} from '@models/events/dto/create-event-ticket-order-session.request';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventType } from '@models/events/event-type.enum';
import { Event } from '@models/events/event.model';
import { BillingAddressType } from '@models/payments/billing-address-type.enum';
import { BillingAddress } from '@models/payments/billing-address.model';
import { TicketVoucherCodeResponse } from '@models/tickets/dto/ticket-voucher-code.response';
import { User } from '@models/users/user.model';
import { OrderOverviewComponent } from '@modules/events/components/event-payment/components/order-overview/order-overview.component';
import { PersonalDataComponent } from '@modules/events/components/event-payment/components/personal-data/personal-data.component';
import { TaxInvoiceDetailsComponent } from '@modules/events/components/event-payment/components/tax-invoice-details/tax-invoice-details.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth/auth.service';
import { EventService } from '@services/events/event.service';
import { NotificationService } from '@services/shared/notification.service';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { ThemeService } from '@services/theme.service';
import {
  StripeCardElementOptions,
  StripeElementLocale,
  StripeElementsOptions,
  StripeError,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { NumberUtils } from '@utils/number-utils';
import { FormsModules } from '@utils/shared-modules';
import {
  StripeCardComponent,
  StripeElementsDirective,
  StripeFactoryService,
  StripePaymentElementComponent,
  StripeServiceInterface,
} from 'ngx-stripe';
import { environment as env } from '@environments/environment';
import { EventAttendeeTicketOrderStatus } from '@models/events/event-attendee-ticket-order-status.enum';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { QuestionType } from '@models/event-attendance/event-attendee-question-type.enum';
import { EventRsvpQuestionComponent } from '../event-rsvp-question/event-rsvp-question.component';
import { BookEventSlotStep } from '@models/events/enums/book-event-slot-step.enum';
import { EventAttendeeQuestion } from '@models/event-attendance/event-attendee-question.model';
import { LegalInfoLinkType } from '@models/legal-info/enums/legal-info-link-type.enum';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { ClaimEventAttendeeTicketRequest } from '@models/events/dto/claim-event-attendee-ticket.request';
import { AccountRequirement } from '@models/events/account-requirement.enum';
import { UserService } from '@services/auth/user.service';
import { OtpVerificationDialog } from '@modules/customer/dialogs/otp-verification/otp-verification.dialog';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-event-payment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModules,
    TranslateModule,
    PersonalDataComponent,
    TaxInvoiceDetailsComponent,
    OrderOverviewComponent,
    StripePaymentElementComponent,
    StripeElementsDirective,
    MatExpansionModule,
    StripeCardComponent,
    MatCheckbox,
    EventRsvpQuestionComponent,
  ],
  providers: [EventService, NotificationService],
  templateUrl: './event-payment.component.html',
  styleUrl: './event-payment.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EventPaymentComponent implements OnInit, OnDestroy {
  stripe?: StripeServiceInterface;

  selectedOption: string = 'email';
  form: FormGroup;
  billingAddressForm?: FormGroup;
  additionalDataForm?: FormGroup;

  private _dataFields?: EventAttendeeQuestion[];
  @Input() set dataFields(dataFields: EventAttendeeQuestion[] | undefined) {
    this._dataFields = dataFields;

    // setup ticket data fields
    if (this.dataFields) {
      this.additionalDataForm = this.fb.group({});
      for (const dataField of this.dataFields) {
        this.additionalDataForm.addControl(
          `question#${dataField.id}`,
          this.fb.control(
            '',
            dataField.isRequired && dataField.type !== QuestionType.CHECKBOX
              ? Validators.required
              : [],
          ),
        );
      }
    } else {
      this.additionalDataForm = undefined;
    }
  }
  get dataFields(): EventAttendeeQuestion[] | undefined {
    return this._dataFields;
  }

  private _ticketToClaim?: EventAttendeeTicket;
  @Input() set ticketToClaim(ticketToClaim: EventAttendeeTicket | undefined) {
    this._ticketToClaim = ticketToClaim;

    if (this.ticketToClaim) {
      this.dataFields =
        this.ticketToClaim.getDataFieldsAsEventAttendeeQuestion();
    }
  }
  get ticketToClaim(): EventAttendeeTicket | undefined {
    return this._ticketToClaim;
  }

  @Input() event?: Event;
  @Input() isGeneric?: boolean = false;
  @Input() isBookASlot = false;
  @Input() isClaimTicket = false;

  private _accountRequirement?: AccountRequirement;
  @Input() set accountRequirement(
    accountRequirement: AccountRequirement | undefined,
  ) {
    if (
      this.accountRequirement !== accountRequirement &&
      (!this.loggedUser || !this.loggedUser.id)
    ) {
      this._accountRequirement = accountRequirement;

      const createAccountVal =
        !!this.accountRequirement &&
        this.accountRequirement !== AccountRequirement.NO_ACCOUNT;

      const createAccountControl = this.form.controls['createAccount'];
      if (createAccountControl) {
        createAccountControl.setValue(createAccountVal);
        if (this.accountRequirement === AccountRequirement.REQUIRED) {
          createAccountControl.disable();
        } else {
          createAccountControl.enable();
        }
      }
    }
  }
  get accountRequirement(): AccountRequirement | undefined {
    return this._accountRequirement;
  }

  @Input() updatedSessionRequest?: CreateEventTicketOrderSessionRequest;
  @Input() canPay: boolean = true;
  @Input() isPreview: boolean = false;
  @Input() bookEventSlotStep?: BookEventSlotStep;
  @Output() nextStep = new EventEmitter<EventAttendeeTicketOrder>();
  @Output() paymentFailed = new EventEmitter<StripeError | undefined>();
  @Output() claimTicketSuccess = new EventEmitter<EventAttendeeTicket>();
  @Output() toPayment = new EventEmitter<void>();
  isDesktop: boolean = false;
  acceptTermsAndConditions: boolean = false;
  acceptDataPrivacy: boolean = false;
  acceptMarketing: boolean = false;

  readonly setPersonalDataOpenState = signal(true);
  readonly setPaymentCardOpenState = signal(true);

  private _ticketOrder?: EventAttendeeTicketOrder;
  private _voucher?: TicketVoucherCodeResponse;
  @Input() set voucher(voucher: TicketVoucherCodeResponse | undefined) {
    this._voucher = voucher;
  }

  @Input() set ticketOrder(ticketOrder: EventAttendeeTicketOrder | undefined) {
    if (ticketOrder) {
      this._ticketOrder = ticketOrder;

      if (!this.stripe) {
        if (
          this.ticketOrder?.stripeConnectedAccountId &&
          this.ticketOrder.stripeConnectedAccountId !== ''
        ) {
          this.stripe = this.stripeFactoryService.create(env.stripePublicKey, {
            stripeAccount: this.ticketOrder?.stripeConnectedAccountId,
          });
        } else {
          this.stripe = this.stripeFactoryService.create(env.stripePublicKey);
        }
      }

      if (this.elementsOptions.appearance) {
        this.setAppearance();
        /* this.elementsOptions.appearance.variables = {
          fontFamily: 'Montserrat',
          fontSizeBase: '1rem',
          colorPrimary: '#e60213',
          colorText: '#c5a66d',
          colorBackground: this.event?.eventTemplate?.visual?.backgroundColor,
          borderRadius: '0',
        }; */
        // const computedStyle = getComputedStyle(document.documentElement);
        // this.elementsOptions.appearance.variables = {
        //   fontFamily: computedStyle.getPropertyValue('var(--body-font-family)'),
        //   fontSizeBase: '1rem',
        //   colorPrimary: computedStyle.getPropertyValue(
        //     '--event-theme-primary-color',
        //   ),
        //   colorText: computedStyle.getPropertyValue(
        //     'var(--event-theme-body-color)',
        //   ),
        //   colorBackground: computedStyle.getPropertyValue(
        //     'var(--event-theme-background-color)',
        //   ),
        //   borderRadius: '0',
        // };
      }

      this.elementsOptions.locale = <StripeElementLocale>(
        this.translateService.currentLang
      );
      this.elementsOptions.customerSessionClientSecret =
        ticketOrder.stripeCustomerSessionClientSecret;
      this.elementsOptions.clientSecret = ticketOrder.stripePaymentIntentSecret;

      if (!this.ticketOrder?.totalPrice) {
        this.removeAllBillingAddressValidators();
      } else {
        const type = this.billingAddressForm?.controls['type'].value;
        this.setBillingAddressValidators(
          type || BillingAddressType.PERSONAL,
          true,
        );
      }
    }
  }

  get voucher(): TicketVoucherCodeResponse | undefined {
    return this._voucher;
  }
  get ticketOrder(): EventAttendeeTicketOrder | undefined {
    return this._ticketOrder;
  }

  toggleTermsAndConditions() {
    this.acceptTermsAndConditions = !this.acceptTermsAndConditions;
  }
  toggleDataPrivacy() {
    this.acceptDataPrivacy = !this.acceptDataPrivacy;
  }

  toggleAcceptMarketing() {
    this.acceptMarketing = !this.acceptMarketing;
  }

  @Input() mainImage?: string;

  loggedUser?: User;

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
    appearance: {
      theme: 'stripe',
      labels: 'floating',
    },
  };

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#282828',
        fontWeight: '400',
        fontFamily: '"Gabarito',
        fontSize: '30px',
        '::placeholder': {
          color: '#282828',
        },
      },
    },
  };

  isStripeElementValid = false;

  accountCreationInProgress = false;
  claimInProgress = false;

  paying = signal(false);

  init = false;

  @ViewChild(StripePaymentElementComponent)
  paymentElement!: StripePaymentElementComponent;

  @ViewChild(StripeCardComponent)
  cardElement!: StripeCardComponent;

  /** Subject that emits when the component has been destroyed. */
  private _onDestroy = new Subject<void>();

  #destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private eventService: EventService,
    private userService: UserService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private screenWidthService: ScreenWidthService,
    private stripeFactoryService: StripeFactoryService,
    private themeService: ThemeService,
    private dialog: MatDialog,
  ) {
    this.themeService.theme$.pipe(takeUntilDestroyed()).subscribe((theme) => {
      this.setCardOptions(theme || 'light');
    });
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.email, Validators.required]],
      agreeToTerms: [false],
      asGuest: [undefined],
    });

    this.screenWidthService
      .isDesktop()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (isDesktop: boolean) => {
          this.isDesktop = isDesktop;
        },
      });
  }

  setBillingAddressValidators(
    type: BillingAddressType,
    setAllValidators = false,
  ): void {
    if (type === BillingAddressType.PERSONAL) {
      this.billingAddressForm?.controls['companyName'].removeValidators(
        Validators.required,
      );
      this.billingAddressForm?.controls['vatNumber'].removeValidators(
        Validators.required,
      );

      this.billingAddressForm?.controls['invoiceRecipient'].setValidators(
        Validators.required,
      );
    } else {
      this.billingAddressForm?.controls['invoiceRecipient'].removeValidators(
        Validators.required,
      );

      this.billingAddressForm?.controls['companyName'].setValidators(
        Validators.required,
      );
      this.billingAddressForm?.controls['vatNumber'].setValidators(
        Validators.required,
      );
    }

    if (setAllValidators) {
      this.billingAddressForm?.controls['address'].setValidators(
        Validators.required,
      );
      this.billingAddressForm?.controls['locality'].setValidators(
        Validators.required,
      );
      this.billingAddressForm?.controls['postalCode'].setValidators(
        Validators.required,
      );
      this.billingAddressForm?.controls['country'].setValidators(
        Validators.required,
      );
    }

    this.billingAddressForm?.controls['companyName'].updateValueAndValidity();
    this.billingAddressForm?.controls['vatNumber'].updateValueAndValidity();
    this.billingAddressForm?.controls[
      'invoiceRecipient'
    ].updateValueAndValidity();
  }

  removeAllBillingAddressValidators(): void {
    if (this.billingAddressForm) {
      Object.keys(this.billingAddressForm?.controls).forEach((key) => {
        const control = this.billingAddressForm?.get(key);
        control?.clearValidators();
        control?.updateValueAndValidity();
      });
    }
  }

  resetBillingAddress() {
    this.resetBillingAddressForm();
  }

  setCardOptions(theme: string) {
    if (theme === 'dark') {
      this.cardOptions = {
        style: {
          base: {
            ...this.cardOptions.style?.base,
            color: '#e8e8e9',
            '::placeholder': {
              color: '#e8e8e9',
            },
          },
        },
      };
    } else {
      this.cardOptions = {
        style: {
          base: {
            ...this.cardOptions.style?.base,
            color: '#282828',
            '::placeholder': {
              color: '#282828',
            },
          },
        },
      };
    }
  }

  resetBillingAddressForm(): void {
    if (this.billingAddressForm) {
      this.billingAddressForm.reset({
        type: this.billingAddressForm.controls['type'].value,
        invoiceRecipient: '',
        companyName: '',
        vatNumber: '',
        address: '',
        addressAddition: '',
        locality: '',
        federalState: '',
        postalCode: '',
        country: '',
      });

      this.billingAddressForm.markAsPristine();
      this.billingAddressForm.markAsUntouched();
    }
  }

  ngOnInit(): void {
    this.setAppearance();

    this.authService.userSubject
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((user) => {
        if (this.loggedUser?.id && !user.id) {
          this.loggedUser = undefined;
        }

        if (user.id && this.loggedUser?.id !== user.id) {
          this.loggedUser = user;

          this.form.controls['name'].setValue(this.loggedUser.name);
          if (this.loggedUser.email !== '') {
            this.form.controls['email'].setValue(this.loggedUser.email);
          }

          this.resetGuestUserControls();
        }

        if (!this.loggedUser || !this.loggedUser.id) {
          const createAccountVal =
            !!this.accountRequirement &&
            this.accountRequirement !== AccountRequirement.NO_ACCOUNT;
          this.form.addControl(
            'createAccount',
            this.fb.control({
              value: createAccountVal,
              disabled: this.accountRequirement === AccountRequirement.REQUIRED,
            }),
          );
          this.form.addControl(
            'password',
            this.fb.control('', createAccountVal ? Validators.required : null),
          );

          this.form.controls['email'].valueChanges
            .pipe(
              takeUntil(this._onDestroy),
              debounceTime(250),
              distinctUntilChanged(),
              tap((val) => {
                if (
                  !this.form.controls['createAccount']?.value ||
                  this.loggedUser?.id
                ) {
                  return;
                }

                if (this.form.controls['email'].valid) {
                  this.checkUserWithEmailExists();
                } else {
                  this.removeCustomEmailError();
                }
              }),
            )
            .subscribe();

          this.form.controls['createAccount'].valueChanges.subscribe((val) => {
            const passwordControl = this.form.controls['password'];
            if (val) {
              passwordControl.setValidators(Validators.required);
            } else {
              passwordControl.clearValidators();
            }

            passwordControl.updateValueAndValidity();
          });
        }

        if (!this.init) {
          this.billingAddressForm = this.fb.group({
            type: [
              this.loggedUser?.billingAddress?.type ||
                BillingAddressType.PERSONAL,
              [Validators.required],
            ],
            invoiceRecipient: [
              this.loggedUser?.billingAddress?.invoiceRecipient || '',
              !this.loggedUser ||
              !this.loggedUser.billingAddress ||
              this.loggedUser.billingAddress.type ===
                BillingAddressType.PERSONAL
                ? [Validators.required]
                : [],
            ],
            companyName: [
              this.loggedUser?.billingAddress?.companyName || '',
              this.loggedUser?.billingAddress?.type ===
              BillingAddressType.COMPANY
                ? [Validators.required]
                : [],
            ],
            vatNumber: [
              this.loggedUser?.billingAddress?.vatNumber || '',
              this.loggedUser?.billingAddress?.type ===
              BillingAddressType.COMPANY
                ? [Validators.required]
                : [],
            ],
            address: [
              this.loggedUser?.billingAddress?.street || '',
              [Validators.required],
            ],
            addressAddition: [
              this.loggedUser?.billingAddress?.streetNumber || '',
            ],
            locality: [
              this.loggedUser?.billingAddress?.locality || '',
              [Validators.required],
            ],
            federalState: [this.loggedUser?.billingAddress?.federalState || ''],
            postalCode: [
              this.loggedUser?.billingAddress?.postalCode || '',
              [Validators.required],
            ],
            country: [
              this.loggedUser?.billingAddress?.country || '',
              [Validators.required],
            ],
          });

          this.billingAddressForm.controls['type'].valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe((val) => {
              if (!this.ticketOrder || this.ticketOrder.totalPrice) {
                this.setBillingAddressValidators(val);
              }
            });
        }

        this.init = true;
      });
  }

  setAppearance() {
    if (this.elementsOptions.appearance && this.event) {
      if (this.event.type === EventType.ADVANCED_ONE_COLUMN) {
        this.elementsOptions.appearance.variables = {
          fontFamily: 'Montserrat',
          fontSizeBase: '1rem',
          colorPrimary: '#e60213',
          colorText: '#c5a66d',
          colorBackground: this.event?.eventTemplate?.visual?.backgroundColor,
          borderRadius: '0',
        };
      } else {
        this.elementsOptions.appearance.variables = {
          fontFamily:
            this.event.eventTemplate?.font?.bodyFont + ',  sans-serif',
          fontSizeBase: '1rem',
          colorPrimary: this.event.eventTemplate?.font?.primaryLogoFontColor,
          colorText: this.event.eventTemplate?.font?.bodyFontColor,
          colorBackground: 'white',
          borderRadius: '14px',
        };

        this.elementsOptions.appearance.rules = {
          '.Input': {
            backgroundColor: '#ffffff',
            color: '#333333',
            borderRadius: '14px',
            fontFamily: this.event.eventTemplate?.font?.bodyFont
              ? this.event.eventTemplate?.font?.bodyFont + ',  sans-serif'
              : 'Montserrat',
            fontSize: '14px',
          },
          '.Label': {
            color: '#495057',
            fontFamily: this.event.eventTemplate?.font?.bodyFont
              ? this.event.eventTemplate?.font?.bodyFont + ',  sans-serif'
              : 'Montserrat',
            fontSize: '14px',
          },
          '.TabLabel': {
            fontSize: '14px',
            fontWeight: '500',
          },
        };
      }
    }
  }
  paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
    },
  };

  getAppearance(): MatFormFieldAppearance {
    if (this.event && this.event.type === EventType.ADVANCED_ONE_COLUMN) {
      return 'fill';
    } else return 'outline';
  }

  selectOption(option: string): void {
    this.selectedOption = option;
  }

  stripeElementsChange(event: any) {
    this.isStripeElementValid = event.complete;
    // if (event.complete) {
    //   this.stripe.createPaymentMethod({elements:this.paymentElement.elements})
    //   .subscribe({
    //     next: (res) => {
    //       if (res.paymentMethod) {
    //         this.eventService.
    //         res.paymentMethod.id
    //       }
    //     },
    //     error: () => {

    //     }
    //   })
    // }
  }

  pay() {
    if (this.isPreview) return;

    if (this.paying()) return;

    if (
      !this.form ||
      !this.billingAddressForm ||
      !this.ticketOrder ||
      !this.stripe
    ) {
      return;
    }

    if (!this.acceptTermsAndConditions) {
      return;
    }

    if (!this.form.valid) {
      this.form.markAllAsTouched();

      if (!this.billingAddressForm.valid) {
        this.billingAddressForm.markAllAsTouched();
      }

      if (this.additionalDataForm && !this.additionalDataForm.valid) {
        this.additionalDataForm.markAllAsTouched();
      }

      return;
    }

    if (!this.billingAddressForm.valid) {
      this.billingAddressForm.markAllAsTouched();
      return;
    }

    if (this.additionalDataForm && !this.additionalDataForm.valid) {
      this.additionalDataForm.markAllAsTouched();
    }

    this.paying.set(true);

    const billingAddressValue = this.billingAddressForm.value;

    const billingAddress: BillingAddress = {
      type: billingAddressValue.type,

      street: billingAddressValue.address,
      streetNumber: billingAddressValue.addressAddition,
      postalCode: billingAddressValue.postalCode,
      locality: billingAddressValue.locality,

      country: billingAddressValue.country,
      federalState: billingAddressValue.federalState,
    };

    if (billingAddressValue.type === BillingAddressType.PERSONAL) {
      billingAddress.invoiceRecipient = billingAddressValue.invoiceRecipient;
    } else {
      billingAddress.companyName = billingAddressValue.companyName;
      billingAddress.vatNumber = billingAddressValue.vatNumber;
    }

    const req: ConfirmEventTicketOrderRequest = {
      uuid: this.ticketOrder.uuid,
      billingAddress: billingAddress,
    };

    req.acceptMarketing = this.acceptMarketing;

    if (this.updatedSessionRequest) {
      req.update = this.updatedSessionRequest;
    }

    const value = this.form.value;

    let name = value.name || '';
    let email = value.email || '';

    if (!this.loggedUser || this.loggedUser.id === 0) {
      req.asGuest = value.asGuest;
      req.email = value.email;

      const guestName = value.name;
      if (guestName && guestName !== '') {
        const nameParts = guestName.trim().split(/\s+/);
        req.firstName = nameParts[0];

        if (nameParts.length > 1) {
          req.lastName = nameParts[1];
        }
      }
    } else {
      name = this.loggedUser.name;
      email = this.loggedUser.email;
    }

    // additional data from data fields
    if (this.additionalDataForm && this.dataFields) {
      const additionalDataValue = this.additionalDataForm.value;
      const answers: EventTicketOrderAdditionalDataRequest[] = [];
      for (const dataField of this.dataFields) {
        if (additionalDataValue[`question#${dataField.id}`]) {
          const answer: EventTicketOrderAdditionalDataRequest = {
            dataFieldId: dataField.id,
          };

          if (dataField.type === QuestionType.MULTISELECT_DROPDOWN) {
            const options = additionalDataValue[`question#${dataField.id}`];
            if (
              !!options &&
              Array.isArray(options) &&
              options.every((item) => typeof item === 'number')
            ) {
              answer.dataFieldOptionIds = options;
            }
          } else if (
            dataField.type === QuestionType.DROPDOWN &&
            !!additionalDataValue[`question#${dataField.id}`]
          ) {
            answer.dataFieldOptionId =
              +additionalDataValue[`question#${dataField.id}`];
          } else if (
            dataField.type === QuestionType.TEXT ||
            dataField.type === QuestionType.TEXTAREA
          ) {
            answer.openAnswer = additionalDataValue[`question#${dataField.id}`];
          } else if (dataField.type === QuestionType.CHECKBOX) {
            answer.boolAnswer =
              !!additionalDataValue[`question#${dataField.id}`];
          } else if (dataField.type === QuestionType.NUMBER) {
            answer.openAnswer =
              additionalDataValue[`question#${dataField.id}`].toString();
          }

          answers.push(answer);
        }
      }

      req.additionalData = answers;
    }

    this.eventService.confirmEventTicketOrder(req).subscribe({
      next: (ticketOrder) => {
        this.ticketOrder = ticketOrder;
        if (this.isGeneric && !this.isBookASlot) {
          if (
            this.ticketOrder.status === EventAttendeeTicketOrderStatus.SUCCEEDED
          ) {
            this.nextStep.emit(ticketOrder);
            return;
          }
          this.stripe
            ?.confirmCardPayment(ticketOrder.stripePaymentIntentSecret, {
              payment_method: {
                card: this.cardElement.element,
                billing_details: {
                  name: name as string,
                  email: email as string,
                  address: {
                    line1: (billingAddress.street as string) || '',
                    postal_code: (billingAddress.postalCode as string) || '',
                    city: (billingAddress.locality as string) || '',
                  },
                },
              },
              return_url: `${env.website}/events/${this.event?.uri}?order=${this.ticketOrder?.uuid}`,
            })
            .subscribe({
              next: (result) => {
                this.paying.set(false);
                if (result.error) {
                  this.paymentFailed.emit(result.error);
                } else {
                  if (result.paymentIntent.status === 'succeeded') {
                    ticketOrder.status =
                      EventAttendeeTicketOrderStatus.SUCCEEDED;
                    this.nextStep.emit(ticketOrder);
                  }
                }
              },
              error: (err) => {
                this.paying.set(false);
                this.paymentFailed.emit();
              },
            });
        } else {
          if (
            this.ticketOrder.status === EventAttendeeTicketOrderStatus.SUCCEEDED
          ) {
            this.nextStep.emit(ticketOrder);
            return;
          }

          this.stripe
            ?.confirmPayment({
              elements: this.paymentElement
                ? this.paymentElement.elements
                : this.cardElement.elements,
              confirmParams: {
                payment_method_data: {
                  billing_details: {
                    name: name as string,
                    email: email as string,
                    address: {
                      line1: (billingAddress.street as string) || '',
                      postal_code: (billingAddress.postalCode as string) || '',
                      city: (billingAddress.locality as string) || '',
                    },
                  },
                },
                return_url: `${env.website}/events/${this.event?.uri}?order=${this.ticketOrder?.uuid}`,
              },
              redirect: 'if_required',
            })
            .subscribe({
              next: (result) => {
                this.paying.set(false);
                if (result.error) {
                  this.paymentFailed.emit(result.error);
                } else {
                  if (result.paymentIntent.status === 'succeeded') {
                    ticketOrder.status =
                      EventAttendeeTicketOrderStatus.SUCCEEDED;
                    this.nextStep.emit(ticketOrder);
                  }
                }
              },
              error: (err) => {
                this.paying.set(false);
                this.paymentFailed.emit();
              },
            });
        }
      },
      error: () => {
        this.paying.set(false);
        this.notificationService.error(
          this.translateService.instant(
            'APP.EVENT_PAYMENT.COULD_NOT_CONFIRM_ORDER',
          ),
        );
      },
    });
  }

  claimTicket() {
    if (this.isPreview) return;

    if (
      !this.form ||
      !this.ticketToClaim ||
      !this.event ||
      this.claimInProgress
    ) {
      return;
    }

    if (!this.acceptTermsAndConditions) {
      return;
    }

    if (!this.form.valid) {
      this.form.markAllAsTouched();

      if (this.additionalDataForm && !this.additionalDataForm.valid) {
        this.additionalDataForm.markAllAsTouched();
      }

      return;
    }

    if (this.additionalDataForm && !this.additionalDataForm.valid) {
      this.additionalDataForm.markAllAsTouched();

      return;
    }

    const req: ClaimEventAttendeeTicketRequest = {
      uuid: this.ticketToClaim.uuid,
    };

    req.acceptMarketing = this.acceptMarketing;

    const value = this.form.getRawValue();

    if (!this.loggedUser || this.loggedUser.id === 0) {
      if (value.createAccount) {
        return this.createUser(true);
      }

      req.asGuest = value.asGuest;
      req.email = value.email;

      const guestName = value.name;
      if (guestName && guestName !== '') {
        const nameParts = guestName.trim().split(/\s+/);
        req.firstName = nameParts[0];

        if (nameParts.length > 1) {
          req.lastName = nameParts[1];
        }
      }
    }

    // additional data from data fields
    if (this.additionalDataForm && this.dataFields) {
      const additionalDataValue = this.additionalDataForm.value;
      const answers: EventTicketOrderAdditionalDataRequest[] = [];
      for (const dataField of this.dataFields) {
        if (additionalDataValue[`question#${dataField.id}`]) {
          const answer: EventTicketOrderAdditionalDataRequest = {
            dataFieldId: dataField.id,
          };

          if (dataField.type === QuestionType.MULTISELECT_DROPDOWN) {
            const options = additionalDataValue[`question#${dataField.id}`];
            if (
              !!options &&
              Array.isArray(options) &&
              options.every((item) => typeof item === 'number')
            ) {
              answer.dataFieldOptionIds = options;
            }
          } else if (
            dataField.type === QuestionType.DROPDOWN &&
            !!additionalDataValue[`question#${dataField.id}`]
          ) {
            answer.dataFieldOptionId =
              +additionalDataValue[`question#${dataField.id}`];
          } else if (
            dataField.type === QuestionType.TEXT ||
            dataField.type === QuestionType.TEXTAREA
          ) {
            answer.openAnswer = additionalDataValue[`question#${dataField.id}`];
          } else if (dataField.type === QuestionType.CHECKBOX) {
            answer.boolAnswer =
              !!additionalDataValue[`question#${dataField.id}`];
          } else if (dataField.type === QuestionType.NUMBER) {
            answer.openAnswer =
              additionalDataValue[`question#${dataField.id}`].toString();
          }

          answers.push(answer);
        }
      }

      req.additionalData = answers;
    }

    this.claimInProgress = true;

    this.eventService
      .claimUnassignedTicket(this.event.id, req)
      .pipe(finalize(() => (this.claimInProgress = false)))
      .subscribe({
        next: (claimedTicket) => {
          this.claimTicketSuccess.emit(claimedTicket);
          this.notificationService.success(
            this.translateService.instant(
              'APP.CLAIM_TICKET_DIALOG.TICKET_CLAIMED',
            ),
          );
        },
        error: (err) => {
          if (
            err &&
            err.error ===
              'the ticket is already assigned to attendee or is being used'
          ) {
            this.notificationService.error(
              this.translateService.instant(
                'APP.CLAIM_TICKET_DIALOG.ALREADY_CLAIMED',
              ),
              this.translateService.instant(
                'APP.CLAIM_TICKET_DIALOG.COULD_NOT_CLAIM_TICKET',
              ),
            );
          } else {
            this.notificationService.error(
              this.translateService.instant(
                'APP.CLAIM_TICKET_DIALOG.COULD_NOT_CLAIM_TICKET',
              ),
            );
          }
        },
      });
  }

  goToPayment() {
    const value = this.form.getRawValue();

    if ((!this.loggedUser || this.loggedUser.id === 0) && value.createAccount) {
      this.createUser();
    } else {
      this.toPayment.emit();
    }
  }

  createUser(isClaimFlow = false) {
    if (this.isPreview) return;

    if (
      !this.form ||
      !this.event ||
      this.loggedUser ||
      this.accountCreationInProgress
    ) {
      return;
    }

    if (isClaimFlow && !this.acceptTermsAndConditions) {
      return;
    }

    if (!this.form.valid) {
      this.form.markAllAsTouched();

      if (this.additionalDataForm && !this.additionalDataForm.valid) {
        this.additionalDataForm.markAllAsTouched();
      }

      return;
    }

    if (this.additionalDataForm && !this.additionalDataForm.valid) {
      this.additionalDataForm.markAllAsTouched();
      return;
    }

    const values = this.form.value;

    this.accountCreationInProgress = true;

    this.userService
      .register(
        values.email,
        values.password,
        values.name,
        this.translateService.currentLang,
      )
      .pipe(finalize(() => (this.accountCreationInProgress = false)))
      .subscribe({
        next: (res) => {
          const dialogRef = this.dialog.open(OtpVerificationDialog, {
            maxWidth: '400px',
            maxHeight: '100vh',
            width: '100%',
            height: 'auto',
            data: {
              userId: res.id,
              email: res.email,
            },
            panelClass: ['normal-dialog', 'new-normal-dialog'],
            backdropClass: '',
          });

          dialogRef.afterClosed().subscribe((res) => {
            if (res && res.logUser) {
              this.authService.login(
                {
                  email: values.email,
                  password: values.password,
                },
                false,
                () => {
                  if (this.isPreview) {
                    return;
                  }

                  if (isClaimFlow) {
                    this.claimTicket();
                  } else {
                    this.toPayment.emit();
                  }
                },
                (denied: any) => {
                  if (denied && denied.message) {
                    if (denied.message === 'account_blocked') {
                      this.form.controls['password'].setErrors({
                        custom: this.translateService.instant(
                          'APP.AUTH.ERRORS.ACCOUNT_BLOCKED',
                        ),
                      });
                    } else if (denied.message === 'unauthorized') {
                      this.form.controls['password'].setErrors({
                        custom: this.translateService.instant(
                          'APP.AUTH.ERRORS.WRONG_PASSWORD',
                        ),
                      });
                    }
                  } else {
                    this.form.controls['password'].setErrors({
                      custom: this.translateService.instant(
                        'APP.AUTH.ERRORS.INVALID_EMAIL_OR_PASSWORD',
                      ),
                    });
                  }
                },
              );
            }
          });
        },
        error: (err) => {
          this.notificationService.error(
            this.translateService.instant('APP.EVENT_RSVP.ERRORS.SIGN_UP'),
            this.translateService.instant('APP.EVENT_RSVP.ERRORS.COULDNT_RSVP'),
          );
        },
      });
  }

  checkUserWithEmailExists(): void {
    if (this.form.controls['email'].invalid) {
      return;
    }

    const email = this.form.controls['email'].value;

    let params: Map<string, any> = new Map<string, any>();
    params.set('isSignUp', true);

    this.authService.userOrAccountWithEmailExists(email, params).subscribe({
      next: (exists: boolean) => {
        if (exists) {
          this.form.controls['email'].setErrors({
            custom: this.translateService.instant(
              'APP.AUTH.ERRORS.EMAIL_EXISTS',
            ),
          });
        } else {
          this.removeCustomEmailError();
        }
      },
    });
  }

  resetGuestUserControls() {
    this.form.removeControl('createAccount');
    this.form.removeControl('password');
    this.removeCustomEmailError();
  }

  removeCustomEmailError() {
    const errors = { ...this.form.controls['email'].errors };
    if (errors) {
      delete errors['custom'];
      if (Object.keys(errors).length === 0) {
        this.form.controls['email'].setErrors(null);
      } else {
        this.form.controls['email'].setErrors(errors);
      }
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  get termsAndConditionsLink(): string {
    const eventLegalTermsLink =
      this.event?.eventTemplate?.legalInfo?.getLinkByType(
        LegalInfoLinkType.TERMS_AND_CONDITIONS,
      );
    if (eventLegalTermsLink && eventLegalTermsLink.link) {
      return eventLegalTermsLink.link;
    }

    return 'https://eventpage.ai/terms-of-use/';
  }

  get dataPrivacyLink(): string {
    const eventLegalPrivacyLink =
      this.event?.eventTemplate?.legalInfo?.getLinkByType(
        LegalInfoLinkType.DATA_PRIVACY,
      );
    if (eventLegalPrivacyLink && eventLegalPrivacyLink.link) {
      return eventLegalPrivacyLink.link;
    }

    return 'https://eventpage.ai/privacy/';
  }

  get isDataValid(): boolean {
    return (
      this.form.valid &&
      !!this.billingAddressForm?.valid &&
      (!this.additionalDataForm || this.additionalDataForm.valid)
    );
  }

  get isClaimDataValid(): boolean {
    return (
      this.form.valid &&
      (!this.additionalDataForm || this.additionalDataForm.valid)
    );
  }

  get isClaimInProgress(): boolean {
    return this.claimInProgress || this.accountCreationInProgress;
  }

  protected readonly NumberUtils = NumberUtils;
  protected readonly EventType = EventType;
  protected readonly BookEventSlotStep = BookEventSlotStep;
}
